import { gql } from 'graphql-request';

import {
  type Mutation,
  type WebRtcStartLiveCaptionsInput,
  type WebRtcMutationsStartLiveCaptionsArgs,
  type WebRtcStopLiveCaptionsInput,
  type WebRtcMutationsStopLiveCaptionsArgs,
  type StopLiveCaptions,
  type StartLiveCaptions,
  type PreCallTestSession,
  WebRtcSessionType,
  WebRtcSessionV2,
  WebRtcMutationsCreateSessionV2Args,
  WebRtcMutationsJoinSessionV2Args,
} from '../services/graphQlTypes.generated';
import { SessionType } from '../webrtc/session/constants';

import { getLogger } from '../libs/logger';

import type {
  WebRtcCreateSessionV2InputWithCredentials,
  WebRtcJoinSessionV2InputWithCredentials,
} from './sessionApi.service';
import { client } from './graphql.service';

const logger = getLogger('sessionApiGqlHelper');

export const WebRtcSessionTypeMap = {
  [SessionType.P2P]: WebRtcSessionType.P2P,
  [SessionType.Routed]: WebRtcSessionType.Routed,
};

const CREATE_SESSION_V2 = gql`
  mutation ($input: WebRtcCreateSessionV2Input!) {
    WebRtc {
      createSessionV2(input: $input) {
        sessionId
        ownershipToken
        joinTokens {
          identityId
          token
        }
      }
    }
  }
`;

const JOIN_SESSION_V2 = gql`
  mutation ($input: WebRtcJoinSessionV2Input!) {
    WebRtc {
      joinSessionV2(input: $input) {
        sessionId
        ownershipToken
        joinTokens {
          identityId
          token
        }
      }
    }
  }
`;

const START_LIVE_CAPTIONS_MUTATION = gql`
  mutation ($input: WebRtcStartLiveCaptionsInput!) {
    WebRtc {
      startLiveCaptions(input: $input) {
        captionsId
      }
    }
  }
`;

const STOP_LIVE_CAPTIONS_MUTATION = gql`
  mutation ($input: WebRtcStopLiveCaptionsInput!) {
    WebRtc {
      stopLiveCaptions(input: $input) {
        success
      }
    }
  }
`;

const CREATE_PRE_CALL_TEST_SESSION_MUTATION = gql`
  mutation {
    WebRtc {
      createPreCallTestSession {
        sessionId
        token
        apiKey
      }
    }
  }
`;

export const createSessionV2 = async (
  createRequest: WebRtcCreateSessionV2InputWithCredentials,
): Promise<WebRtcSessionV2 | undefined> => {
  const { ...body } = createRequest;

  try {
    const result = await client.request<
      Mutation,
      WebRtcMutationsCreateSessionV2Args
    >(
      CREATE_SESSION_V2,
      {
        input: {
          identityIds: body.identityIds,
          sessionType: body.sessionType,
        },
      },
      {
        Authorization: `Bearer ${body.credentials.token}`,
      },
    );

    return result.WebRtc.createSessionV2;
  } catch (error) {
    logger.error(
      '[createSessionV2] Request to graphql createSessionV2 endpoint failed',
    );
    throw error;
  }
};

export const joinSessionV2 = async (
  joinRequest: WebRtcJoinSessionV2InputWithCredentials,
): Promise<WebRtcSessionV2 | undefined> => {
  const { ...body } = joinRequest;

  try {
    const result = await client.request<
      Mutation,
      WebRtcMutationsJoinSessionV2Args
    >(
      JOIN_SESSION_V2,
      {
        input: {
          ownershipToken: body.ownershipToken,
          identityIds: body.identityIds,
        },
      },
      {
        Authorization: `Bearer ${body.credentials.token}`,
      },
    );

    return result.WebRtc.joinSessionV2;
  } catch (error) {
    logger.error(
      '[joinSessionV2] Request to graphql joinSessionV2 endpoint failed',
    );
    throw error;
  }
};

export const createPreCallTestSession =
  async (): Promise<PreCallTestSession> => {
    try {
      const result = await client.request<Mutation>(
        CREATE_PRE_CALL_TEST_SESSION_MUTATION,
      );

      return result.WebRtc.createPreCallTestSession;
    } catch (error) {
      logger.error(
        '[createPreCallTestSession] Request to graphql createPreCallTestSession endpoint failed',
      );
      throw error;
    }
  };

export const startLiveCaptions = async (
  input: WebRtcStartLiveCaptionsInput,
): Promise<StartLiveCaptions> => {
  try {
    const result = await client.request<
      Mutation,
      WebRtcMutationsStartLiveCaptionsArgs
    >(START_LIVE_CAPTIONS_MUTATION, {
      input,
    });

    return result.WebRtc.startLiveCaptions;
  } catch (err) {
    logger.error(
      '[startLiveCaptions] Request to graphql startLiveCaptions endpoint failed',
    );
    throw err;
  }
};

export const stopLiveCaptions = async (
  input: WebRtcStopLiveCaptionsInput,
): Promise<StopLiveCaptions> => {
  try {
    const result = await client.request<
      Mutation,
      WebRtcMutationsStopLiveCaptionsArgs
    >(STOP_LIVE_CAPTIONS_MUTATION, {
      input,
    });

    return result.WebRtc.stopLiveCaptions;
  } catch (err) {
    logger.error(
      '[stopLiveCaptions] Request to graphql stopLiveCaptions endpoint failed',
    );
    throw err;
  }
};
